import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  FormControl,
  FormLabel,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Select,
  Stack,
  Textarea,
  useToast,
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { Operation, OperationType } from "../../../../entity/Operation";
import { Product } from "../../../../entity/Product";
import { useSelector } from "react-redux";
import { selectProducts } from "../../../../store/selectors/ProductSelector";
import { t } from "i18next";
import { selectStore } from "../../../../store/selectors/StoreSelector";
import { VSwitchButton } from "../../../components/switch/VSwitchButton";
import "./AddNewoperation.css";
import ReactSelect, { StylesConfig } from "react-select";

interface AddNewOperationProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (operation: Operation) => Promise<void>;
  operation?: Operation;
}

interface OperationOption {
  value: OperationType;
  label: string;
  type: string;
}

export const AddNewOperation = ({
  isOpen,
  onClose,
  onCreate,
  operation,
}: AddNewOperationProps) => {
  const products = useSelector(selectProducts);
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();

  const store = useSelector(selectStore);

  const [isCredit, setIsCredit] = useState(true);
  const [isChangeStock, setIsChangeStock] = useState(false);
  const [formData, setFormData] = useState<Operation>(
    operation || {
      id: "",
      productId: "",
      quantity: 0,
      description: "",
      amount: 0,
      type: "BUY",
      date: new Date(),
      storeId: store?.id!!,
    },
  );

  useEffect(() => {
    if (operation) {
      setFormData(operation);
    } else {
      setFormData({
        id: "",
        productId: "",
        quantity: 0,
        description: "",
        amount: 0,
        type: "BUY",
        date: new Date(),
        storeId: store?.id!!,
      });
    }
  }, [operation, store?.id]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleNumberChange = (
    name: string,
    valueAsString: string,
    valueAsNumber: number,
  ) => {
    setFormData((prev) => ({
      ...prev,
      [name]: valueAsNumber,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await onCreate(formData);
      toast({
        title: "Operation created",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      onClose();
      setFormData({
        id: "",
        productId: "",
        quantity: 0,
        description: "",
        amount: 0,
        type: "BUY",
        date: new Date(),
        storeId: store?.id!!,
      });
    } catch (error) {
      console.error("Error creating operation:", error);
      toast({
        title: "Error creating operation",
        description:
          error instanceof Error ? error.message : "An error occurred",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangeOperationType = (value: string) => {
    if (value === OperationType.SELL || value === OperationType.BUY) {
      setIsChangeStock(true);
    } else {
      setIsChangeStock(false);
    }
    setFormData((prev) => ({
      ...prev,
      type: value,
    }));
  };

  const selectStyles: StylesConfig<OperationOption> = {
    option: (base, state) => ({
      ...base,
      padding: "12px 16px",
      margin: "4px 0",
      borderRadius: "8px",
      background: state.isSelected
        ? "linear-gradient(135deg, #0d6efd 0%, #0b5ed7 100%)"
        : state.isFocused
          ? "linear-gradient(135deg, #e9ecef 0%, #dee2e6 100%)"
          : "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
      color: state.isSelected ? "white" : "inherit",
      border: "1px solid #dee2e6",
      cursor: "pointer",
      fontWeight: "500",
      transition: "all 0.2s ease",
      borderLeft:
        state.data?.type === "SELL"
          ? "4px solid #28a745"
          : state.data?.type === "BUY"
            ? "4px solid #dc3545"
            : state.data?.type === "TRANSFER_IN"
              ? "4px solid #17a2b8"
              : state.data?.type === "OTHER_TRANSFER_OUT"
                ? "4px solid #568a5dff"
                : state.data?.type === "TEAM_SALARY"
                  ? "4px solid #fd7e14"
                  : state.data?.type === "ADS"
                    ? "4px solid #ffc107"
                    : "4px solid transparent",
      "&:hover": {
        transform: "translateY(-1px)",
        boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
      },
    }),
    control: (base) => ({
      ...base,
      border: "1px solid #dee2e6",
      borderRadius: "8px",
      minHeight: "48px",
      padding: "2px 4px",
    }),
    menu: (base) => ({
      ...base,
      borderRadius: "8px",
      boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
      border: "1px solid #dee2e6",
    }),
    singleValue: (base) => ({
      ...base,
      fontWeight: "500",
    }),
  };

  const [operationOptions, setOperationOptions] = useState<OperationOption[]>(
    [],
  );

  useEffect(() => {
    if (isCredit) {
      setOperationOptions([
        { value: OperationType.SELL, label: t("operation.sell"), type: "SELL" },
        {
          value: OperationType.TRANSFER_IN,
          label: t("operation.transfer_in"),
          type: "TRANSFER_IN",
        },
      ]);
    } else {
      setOperationOptions([
        { value: OperationType.BUY, label: t("operation.buy"), type: "BUY" },

        {
          value: OperationType.TEAM_SALARY,
          label: t("operation.team_salary"),
          type: "TEAM_SALARY",
        },
        { value: OperationType.ADS, label: t("operation.ads"), type: "ADS" },

        {
          value: OperationType.OTHER_TRANSFER_OUT,
          label: t("operation.other_transfer_out"),
          type: "OTHER_TRANSFER_OUT",
        },
      ]);
    }
  }, [isCredit]);

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="xl">
      <DrawerOverlay />

      <DrawerContent
        borderTopLeftRadius="10px"
        borderBottomLeftRadius="10px"
        overflow="hidden"
      >
        <DrawerCloseButton />
        <form onSubmit={handleSubmit}>
          <DrawerBody>
            <Stack spacing={4}>
              <VSwitchButton
                labelFirst={t("operation.credit") + " (+)"}
                labelSecond={t("operation.debit") + " (-)"}
                onChange={(value) => {
                  setIsCredit(value);
                }}
              />

              <FormControl>
                <ReactSelect
                  value={operationOptions.find(
                    (option) => option.value === formData.type,
                  )}
                  onChange={(selectedOption) =>
                    handleChangeOperationType(
                      (selectedOption as OperationOption).value,
                    )
                  }
                  options={operationOptions}
                  styles={selectStyles}
                  placeholder={t("operation.select_operation_type")}
                  isSearchable={false}
                  className="operation-type-select"
                  classNamePrefix="react-select"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>{t("operation.amount")}</FormLabel>
                <NumberInput
                  min={0}
                  precision={2}
                  value={formData.amount}
                  onChange={(valueString, valueNumber) =>
                    handleNumberChange("amount", valueString, valueNumber)
                  }
                  size={{ base: "md", md: "lg" }}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>
              {isChangeStock && (
                <>
                  {" "}
                  <FormControl>
                    <FormLabel>{t("operation.product")}</FormLabel>
                    <Select
                      placeholder="Select product"
                      name="productId"
                      value={formData.productId}
                      onChange={(e) => handleChange(e)}
                      size={{ base: "md", md: "lg" }}
                    >
                      {products.map((product: Product) => (
                        <option key={product.id} value={product.id}>
                          {product.name} ({t("operation.stock")}:{" "}
                          {product.quantity})
                        </option>
                      ))}
                      <option key="transfer" value="transfer">
                        {t("operation.transfer")}
                      </option>
                    </Select>
                  </FormControl>
                  <FormControl>
                    <FormLabel>{t("operation.quantity")}</FormLabel>
                    <NumberInput
                      min={1}
                      value={formData.quantity}
                      onChange={(valueString, valueNumber) =>
                        handleNumberChange("quantity", valueString, valueNumber)
                      }
                      size={{ base: "md", md: "lg" }}
                    >
                      <NumberInputField />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>
                </>
              )}

              <FormControl>
                <FormLabel>{t("operation.description")}</FormLabel>
                <Textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder={t("operation.enterDescription")}
                  size={{ base: "md", md: "lg" }}
                />
              </FormControl>
            </Stack>
          </DrawerBody>
          <DrawerFooter>
            <Button
              variant="outline"
              mr={3}
              onClick={onClose}
              size={{ base: "md", md: "lg" }}
              width={{ base: "100%", md: "auto" }}
              mb={{ base: 2, md: 0 }}
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              isLoading={isLoading}
              loadingText={t("common.creating")}
              size={{ base: "md", md: "lg" }}
              width={{ base: "100%", md: "auto" }}
            >
              {t("common.create")}
            </Button>
          </DrawerFooter>
        </form>
      </DrawerContent>
    </Drawer>
  );
};
